import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api, { fmtTL, EXPENSE_CATEGORIES } from "@/lib/api";
import { Home, Phone, Fuel, ShoppingCart, Landmark, Sparkles, Wrench, Receipt, Plus, Trash2, Filter, Banknote } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const ICONS = { kira: Home, telefon: Phone, yakit: Fuel, market: ShoppingCart, vergi: Landmark, temizlik: Sparkles, bakim: Wrench, diger: Receipt };
const today = () => new Date().toISOString().slice(0, 10);
const emptyForm = { title: "", category: "kira", branch_id: "", amount: "", expense_date: "" };

export default function Masraflar() {
  const qc = useQueryClient();
  const [filter, setFilter] = useState("all");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ ...emptyForm, expense_date: today() });

  const { data: expenses = [] } = useQuery({ queryKey: ["expenses"], queryFn: async () => (await api.get("/expenses")).data });
  const { data: branches = [] } = useQuery({ queryKey: ["branches"], queryFn: async () => (await api.get("/branches")).data });

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["expenses"] });
    qc.invalidateQueries({ queryKey: ["bills"] });
    qc.invalidateQueries({ queryKey: ["dashboard"] });
  };

  const createM = useMutation({
    mutationFn: (b) => api.post("/expenses", b),
    onSuccess: () => { invalidate(); setOpen(false); toast.success("Masraf eklendi"); setForm({ ...emptyForm, expense_date: today() }); },
    onError: () => toast.error("Masraf eklenemedi"),
  });
  const delM = useMutation({ mutationFn: (id) => api.delete(`/expenses/${id}`), onSuccess: () => { invalidate(); toast.success("Masraf silindi"); } });

  const branchName = (id) => branches.find((b) => b.id === id)?.name || "—";
  const filtered = filter === "all" ? expenses : expenses.filter((e) => e.category === filter);
  const total = filtered.reduce((s, e) => s + e.amount, 0);

  const submit = (e) => { e.preventDefault(); createM.mutate({ ...form, amount: parseFloat(form.amount) }); };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-bold text-white tracking-tight flex items-center gap-3">
            <Banknote className="w-8 h-8 text-orange-400" /> Masraflar
          </h1>
          <p className="text-slate-400 text-sm mt-1">Kira, telefon, yakıt, market gibi kalem giderler — bilançoya otomatik yansır</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <button data-testid="add-expense-button" className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-500 text-white text-sm font-semibold px-4 py-2.5 rounded-xl transition-colors">
              <Plus className="w-4 h-4" /> Masraf Ekle
            </button>
          </DialogTrigger>
          <DialogContent className="bg-[#0D1220] border-slate-800 text-white">
            <DialogHeader><DialogTitle className="font-heading">Yeni Masraf</DialogTitle></DialogHeader>
            <form onSubmit={submit} className="space-y-4">
              <div>
                <label className="text-xs uppercase tracking-widest text-slate-400">Masraf Kalemi</label>
                <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                  <SelectTrigger data-testid="expense-category-select" className="mt-1.5 bg-slate-900 border-slate-700"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#0D1220] border-slate-700 text-white">
                    {Object.entries(EXPENSE_CATEGORIES).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs uppercase tracking-widest text-slate-400">Açıklama</label>
                <input data-testid="expense-title-input" required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="mt-1.5 w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-blue-500" placeholder="Örn. Eylül ayı kirası" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs uppercase tracking-widest text-slate-400">Tutar (₺)</label>
                  <input data-testid="expense-amount-input" required type="number" step="0.01" min="0" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className="mt-1.5 w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-blue-500" />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-widest text-slate-400">Tarih</label>
                  <input data-testid="expense-date-input" required type="date" value={form.expense_date} onChange={(e) => setForm({ ...form, expense_date: e.target.value })} className="mt-1.5 w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-blue-500 [color-scheme:dark]" />
                </div>
              </div>
              <div>
                <label className="text-xs uppercase tracking-widest text-slate-400">Şube</label>
                <Select value={form.branch_id} onValueChange={(v) => setForm({ ...form, branch_id: v })}>
                  <SelectTrigger data-testid="expense-branch-select" className="mt-1.5 bg-slate-900 border-slate-700"><SelectValue placeholder="Seç" /></SelectTrigger>
                  <SelectContent className="bg-[#0D1220] border-slate-700 text-white">
                    {branches.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}{b.type === "merkez" ? " (Merkez)" : ""}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <DialogFooter>
                <button data-testid="expense-submit-button" type="submit" disabled={createM.isPending} className="w-full bg-blue-600 hover:bg-blue-500 text-white font-semibold py-2.5 rounded-lg text-sm">Kaydet</button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Total */}
      <div className="glass rounded-2xl p-5 flex items-center justify-between" data-testid="expense-total-card">
        <div>
          <div className="text-xs uppercase tracking-widest text-slate-400">{filter === "all" ? "Toplam Masraf" : `${EXPENSE_CATEGORIES[filter]?.label} Toplamı`}</div>
          <div className="text-2xl font-heading font-bold text-white tabular mt-1">{fmtTL(total)}</div>
        </div>
        <div className="text-xs text-slate-500">{filtered.length} kayıt</div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-slate-500 text-xs flex items-center gap-1 mr-1"><Filter className="w-3.5 h-3.5" /> Filtre:</span>
        <button data-testid="filter-all" onClick={() => setFilter("all")} className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${filter === "all" ? "bg-blue-600 text-white" : "bg-slate-800/60 text-slate-400 hover:text-white"}`}>Tümü</button>
        {Object.entries(EXPENSE_CATEGORIES).map(([k, v]) => (
          <button key={k} data-testid={`filter-${k}`} onClick={() => setFilter(k)} className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${filter === k ? "text-white" : "bg-slate-800/60 text-slate-400 hover:text-white"}`} style={filter === k ? { background: v.color } : {}}>{v.label}</button>
        ))}
      </div>

      {/* List */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {filtered.map((e) => {
          const cat = EXPENSE_CATEGORIES[e.category] || EXPENSE_CATEGORIES.diger;
          const Icon = ICONS[e.category] || Receipt;
          return (
            <div key={e.id} data-testid={`expense-card-${e.id}`} className="glass rounded-2xl p-5">
              <div className="flex items-start justify-between">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: `${cat.color}20`, color: cat.color }}>
                  <Icon className="w-5 h-5" />
                </div>
                <button data-testid={`delete-expense-${e.id}`} onClick={() => delM.mutate(e.id)} className="text-slate-500 hover:text-rose-400 transition-colors"><Trash2 className="w-4 h-4" /></button>
              </div>
              <div className="mt-4 text-sm font-semibold text-white">{e.title}</div>
              <div className="text-xs text-slate-400">{cat.label} · {branchName(e.branch_id)}</div>
              <div className="mt-3 text-xl font-heading font-bold text-white tabular">{fmtTL(e.amount)}</div>
              <div className="text-xs text-slate-500 mt-1">Tarih: {e.expense_date || "—"}</div>
            </div>
          );
        })}
      </div>
      {filtered.length === 0 && <div className="text-slate-500 text-sm py-16 text-center">Bu filtreye uygun masraf yok. "Masraf Ekle" ile başlayın.</div>}
    </div>
  );
}
